import React, { useState, useRef } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Alert,
  Progress,
  Badge,
  Table,
  Spinner,
  Input,
  InputGroup,
  InputGroupText
} from 'reactstrap';
import axios from 'axios';
import { getEncryptedAccessToken } from '../../services/Utils';
import * as XLSX from 'xlsx';
import cogoToast from 'cogo-toast';
 
const UploadBnf = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [batchResults, setBatchResults] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [beneficiaryCount, setBeneficiaryCount] = useState(0);
  const [componentId, setComponentId] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [rawJsonData, setRawJsonData] = useState([]);
  const [skipUploadedStatus, setSkipUploadedStatus] = useState(true); // Default: skip status 10001
  const [skippedCount, setSkippedCount] = useState(0);
  const [failedRecords, setFailedRecords] = useState([]); // Store failed records with original data
  const fileInputRef = useRef(null);

  const filterData = (data, componentIdFilter, skipStatus10001 = true) => {
    let filtered = data;
    
    // Filter by component_id if provided
    if (componentIdFilter && componentIdFilter.trim() !== '') {
      filtered = filtered.filter(item => {
        const itemComponentId = item.component_id || '';
        return itemComponentId.toString().toLowerCase() === componentIdFilter.toString().toLowerCase().trim();
      });
    }
    
    // Filter out status 10001 (already uploaded) if enabled
    if (skipStatus10001) {
      const skipped = filtered.filter(item => {
        // Check status in multiple possible locations
        const status = item.status || 
                      item.reg_status?.step5 || 
                      item.reg_status?.step1 || 
                      item.reg_status || 
                      null;
        // Convert to string for comparison to handle both number and string formats
        const statusStr = status?.toString();
        return statusStr === "10001";
      }).length;
      setSkippedCount(skipped);
      
      filtered = filtered.filter(item => {
        // Check status in multiple possible locations
        const status = item.status || 
                      item.reg_status?.step5 || 
                      item.reg_status?.step1 || 
                      item.reg_status || 
                      null;
        // Convert to string for comparison to handle both number and string formats
        const statusStr = status?.toString();
        return statusStr !== "10001";
      });
    } else {
      setSkippedCount(0);
    }
    
    return filtered;
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/json') {
      setSelectedFile(file);
      setResults([]);
      setBatchResults([]);
      setCurrentBatch(0);
      setTotalBatches(0);
      setBeneficiaryCount(0);
      setFilteredData([]);
      
      try {
        // Read and parse the file to count beneficiaries
        const fileContent = await file.text();
        const jsonData = JSON.parse(fileContent);
        
        if (Array.isArray(jsonData)) {
          setRawJsonData(jsonData);
          setTotalDataCount(jsonData.length);
          // Filter by component_id and status if enabled
          const filtered = filterData(jsonData, componentId, skipUploadedStatus);
          setFilteredData(filtered);
          const count = filtered.length;
          const batches = Math.ceil(count / 1); // batchSize is 1
          setBeneficiaryCount(count);
          setTotalBatches(batches);
        } else {
          setBeneficiaryCount(0);
          setTotalBatches(0);
          setTotalDataCount(0);
          setRawJsonData([]);
        }
      } catch (error) {
        console.error('Error reading file:', error);
        setBeneficiaryCount(0);
        setTotalBatches(0);
        setTotalDataCount(0);
        setRawJsonData([]);
        setFilteredData([]);
      }
    } else {
      alert('Please select a valid JSON file');
    }
  };

  const handleComponentIdChange = (event) => {
    const newComponentId = event.target.value;
    setComponentId(newComponentId);
    
    // Re-filter if data is already loaded
    if (rawJsonData.length > 0) {
      const filtered = filterData(rawJsonData, newComponentId, skipUploadedStatus);
      setFilteredData(filtered);
      const count = filtered.length;
      const batches = Math.ceil(count / 1);
      setBeneficiaryCount(count);
      setTotalBatches(batches);
    }
  };

  const handleSkipUploadedStatusChange = (event) => {
    const newValue = event.target.checked;
    setSkipUploadedStatus(newValue);
    
    // Re-filter if data is already loaded
    if (rawJsonData.length > 0) {
      const filtered = filterData(rawJsonData, componentId, newValue);
      setFilteredData(filtered);
      const count = filtered.length;
      const batches = Math.ceil(count / 1);
      setBeneficiaryCount(count);
      setTotalBatches(batches);
    }
  };

  const processBatch = async (batch, batchIndex) => {
    try {
      // Get the decrypted access token
      const accessToken = getEncryptedAccessToken();
      
      // Clean up and transform the data before sending to API
      const cleanedBatch = batch.map(item => {
        const isPlainObject = (v) =>
          v !== null && typeof v === "object" && !Array.isArray(v);

        // Store original beneficiary details for reference
        const canProceedSource =
          item.criteria_details?.can_proceed ??
          item.ben_details?.can_proceed ??
          item.criteria_summary?.can_proceed ??
          null;

        const originalDetails = {
          sync_token: item.sync_token,
          name: `${item.ben_details?.first_name || ''} ${item.ben_details?.middle_name || ''} ${item.ben_details?.last_name || ''}`.trim(),
          phone: item.ben_details?.telephone || item.telephone || '',
          location: `${item.ben_details?.village_name || ''} -> ${item.ben_details?.district_name || ''}`.trim(),
          can_proceed: canProceedSource
        };
        
        // Prefer the full criteria_details object from the JSON (it contains all scoring fields)
        const fullCriteriaDetails = isPlainObject(item.criteria_details)
          ? item.criteria_details
          : {};

        const computedCanProceed =
          item.criteria_details?.can_proceed ??
          item.ben_details?.can_proceed ??
          item.criteria_summary?.can_proceed ??
          false;

        // Transform the data structure to match the expected API format
        const cleanedItem = {
          "sync_token": item.sync_token,
          "beneficiaryregistrationproject_id": null,
          "beneficiary_details": {
            "beneficiary_form_details": {
              "country": item.ben_details?.country || "2",
              "zone": item.ben_details?.zone || "",
              "region": item.ben_details?.region || "",
              "district": item.ben_details?.district || "",
              "village": item.ben_details?.village || "",
              "camp": item.ben_details?.camp || "",
              "village_name": item.ben_details?.village_name || "",
              "district_name": item.ben_details?.district_name || "",
              "camp_name": item.ben_details?.camp_name || "",
              "first_name": item.ben_details?.first_name || "",
              "gender": item.ben_details?.gender || "",
              "middle_name": item.ben_details?.middle_name || "",
              "last_name": item.ben_details?.last_name || "",
              "telephone": item.ben_details?.telephone || item.telephone || "",
              "country_code": "252", // Always set to 252
              "total_hh": item.ben_details?.total_hh || {},
              "saad": item.ben_details?.saad || {},
              "saad-total": item.ben_details?.["saad-total"] || {},
              "other_name": item.ben_details?.other_name || null,
              "age": item.ben_details?.age || "",
              "children": item.ben_details?.children || []
            },
            // Don't use `||` here; false is a valid value
            "can_proceed": item.ben_details?.can_proceed ?? item.criteria_details?.can_proceed ?? false
          },
          "country_id": item.country_id || 2,
          "registration_datetime": item.reg_datetime || item.registration_datetime || "",
          "user_id": item.usertoken || item.user_id || "",
          "imei": item.imei || null,
          "channel": item.channel || 700,
          "ip_address": item.ip_address || null,
          "location": item.location || null,
          "version": item.version || 0,
          "startedOn": item.startedOn || "",
          "endedOn": item.endedOn || "",
          "SubjectId": item.SubjectId || "",
          "component_id": item.component_id || "",
          "card_number": item.card_number || null,
          "card_label": item.card_label || null,
          // Send the full criteria_details object (not just can_proceed)
          "criteria_details": {
            ...fullCriteriaDetails,
            // Ensure can_proceed is always present and boolean-ish
            "can_proceed": computedCanProceed
          },
          "selection_criteria_id": item.selection_criteria_id || null,
          "isPhoneValidatedOnline": item.isPhoneValidatedOnline || false,
          "isTelephoneUniquenessGuranteed": item.isTelephoneUniquenessGuranteed || false,
          "telephone": item.telephone || item.ben_details?.telephone || "",
          "country_code": "252", // Always set to 252
          "reg_status": item.reg_status || {},
          "current_step": item.current_step || 2,
          "uniquenessGuranteedMessage": item.uniquenessGuranteedMessage || "",
          "message": item.message || null
        };
        
        return { cleanedItem, originalDetails };
      });
      
      // Extract cleaned items for API call
      const cleanedItems = cleanedBatch.map(item => item.cleanedItem);
      // Create map of sync_token to original batch item for failed records
      const originalItemMap = batch.reduce((acc, item) => {
        acc[item.sync_token] = item; // Store original item from batch
        return acc;
      }, {});
      const originalDetailsMap = cleanedBatch.reduce((acc, item) => {
        acc[item.cleanedItem.sync_token] = item.originalDetails;
        return acc;
      }, {});
      
      // Debug: Log the cleaned batch to see what's being sent
      console.log('Sending batch to API:', JSON.stringify(cleanedItems, null, 2));
      
      const response = await axios.post('https://api.irw-ebms-ear.co.ke/api/web/v1/br/save-beneficiary', cleanedItems, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Check if any individual items in the data array have failed status
      const hasFailures = response.data.data && response.data.data.some(item => item.status === 10002);
      
      // Collect failed records (status 10002) with their original data
      const failedItems = (response.data.data || []).filter(item => item.status === 10002);
      if (failedItems.length > 0) {
        const failedRecordsToAdd = failedItems.map(item => {
          const originalItem = originalItemMap[item.sync_token];
          return originalItem || null; // Return original item if found
        }).filter(item => item !== null); // Remove any null entries
        
        if (failedRecordsToAdd.length > 0) {
          setFailedRecords(prev => [...prev, ...failedRecordsToAdd]);
        }
      }
      
      // Map response data with original beneficiary details
      const enrichedData = (response.data.data || []).map(item => ({
        ...item,
        originalDetails: originalDetailsMap[item.sync_token] || {
          sync_token: item.sync_token,
          name: 'Unknown',
          phone: 'Unknown',
          location: 'Unknown'
        }
      }));
      
      const batchResult = {
        batchIndex: batchIndex + 1,
        status: hasFailures ? 10002 : response.data.status, // Use 10002 if any item failed, otherwise use response status
        message: response.data.message,
        data: enrichedData
      };

      setBatchResults(prev => [...prev, batchResult]);
      
      return batchResult;
    } catch (error) {
      const errorResult = {
        batchIndex: batchIndex + 1,
        status: 10002,
        message: 'Request failed',
        data: [{
          message: error.response?.data?.message || error.message || 'Unknown error',
          status: 10002
        }]
      };
      
      setBatchResults(prev => [...prev, errorResult]);
      return errorResult;
    }
  };

  const processFile = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    // Use filtered data if available, otherwise filter from raw data or read from file
    let dataToProcess = filteredData;
    
    if (dataToProcess.length === 0) {
      if (rawJsonData.length > 0) {
        // Use stored raw data
        dataToProcess = filterData(rawJsonData, componentId, skipUploadedStatus);
      } else if (selectedFile) {
        // Fallback to reading file
        try {
          const fileContent = await selectedFile.text();
          const jsonData = JSON.parse(fileContent);
          if (Array.isArray(jsonData)) {
            dataToProcess = filterData(jsonData, componentId, skipUploadedStatus);
          }
        } catch (error) {
          alert('Error reading file: ' + error.message);
          return;
        }
      }
    }

    if (dataToProcess.length === 0) {
      alert('No data to process. Please check your component_id filter or file selection.');
      return;
    }

    setIsProcessing(true);
    setIsUploading(true);
    setResults([]);
    setBatchResults([]);
    setCurrentBatch(0);
    setFailedRecords([]); // Clear failed records when starting new process

    try {
      if (!Array.isArray(dataToProcess)) {
        throw new Error('Data must be an array of beneficiary data');
      }

      // Split filtered data into batches of 1
      const batchSize = 1;
      const batches = [];
      for (let i = 0; i < dataToProcess.length; i += batchSize) {
        batches.push(dataToProcess.slice(i, i + batchSize));
      }

      setTotalBatches(batches.length);
      setProcessingProgress(0);

      // Process each batch
      for (let i = 0; i < batches.length; i++) {
        setCurrentBatch(i + 1);
        const batchResult = await processBatch(batches[i], i);
        
        // Update progress
        const progress = ((i + 1) / batches.length) * 100;
        setProcessingProgress(progress);
        
        // Add a small delay between batches to avoid overwhelming the server
        if (i < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

    } catch (error) {
      alert(`Error processing file: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setIsUploading(false);
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setResults([]);
    setBatchResults([]);
    setCurrentBatch(0);
    setTotalBatches(0);
    setBeneficiaryCount(0);
    setProcessingProgress(0);
    setComponentId('');
    setFilteredData([]);
    setTotalDataCount(0);
    setRawJsonData([]);
    setSkippedCount(0);
    setFailedRecords([]); // Clear failed records on reset
    // Don't reset skipUploadedStatus - keep user preference
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadFailedRecords = () => {
    if (failedRecords.length === 0) {
      alert('No failed records to download.');
      return;
    }

    // Create JSON string from failed records
    const jsonString = JSON.stringify(failedRecords, null, 2);
    
    // Create blob and download link
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `failed_records_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status) => {
    if (status === 10001) {
      return <Badge color="success">Success</Badge>;
    } else if (status === 10002) {
      return <Badge color="danger">Failed</Badge>;
    } else {
      return <Badge color="warning">Unknown</Badge>;
    }
  };

  const getStatusColor = (status) => {
    if (status === 10001) return 'success';
    if (status === 10002) return 'danger';
    return 'warning';
  };

  const getTotalStats = () => {
    const totalProcessed = batchResults.length;
    
    // Count individual items, not just batches
    let totalItems = 0;
    let successfulItems = 0;
    let failedItems = 0;
    
    batchResults.forEach(batch => {
      if (batch.data && batch.data.length > 0) {
        // Count individual items in the data array
        batch.data.forEach(item => {
          totalItems++;
          if (item.status === 10001) {
            successfulItems++;
          } else if (item.status === 10002) {
            failedItems++;
          }
        });
      } else {
        // If no data array, use batch status
        totalItems++;
        if (batch.status === 10001) {
          successfulItems++;
        } else if (batch.status === 10002) {
          failedItems++;
        }
      }
    });
    
    return { 
      totalProcessed, 
      successful: successfulItems, 
      failed: failedItems,
      totalItems 
    };
  };

  const stats = getTotalStats();

  const exportToExcel = () => {
    if (!batchResults || batchResults.length === 0) {
      cogoToast.error("No data available to export", {
        hideAfter: 3,
      });
      return;
    }

    try {
      // Flatten all batch results into a single array
      const excelData = [];
      
      batchResults.forEach((batch, batchIndex) => {
        if (batch.data && batch.data.length > 0) {
          // If batch has individual items, export each item
          batch.data.forEach((item, itemIndex) => {
            excelData.push({
              'Batch #': batch.batchIndex,
              'Item #': itemIndex + 1,
              'Name': item.originalDetails?.name || 'Unknown',
              'Phone': item.originalDetails?.phone || 'Unknown',
              'Location': item.originalDetails?.location || 'Unknown',
              'Sync Token': item.sync_token || 'N/A',
              'Status': item.status === 10001 ? 'Success' : item.status === 10002 ? 'Failed' : 'Unknown',
              'Status Code': item.status || batch.status,
              'Message': item.message || batch.message || 'No message',
              'Batch Status': batch.status === 10001 ? 'Success' : batch.status === 10002 ? 'Failed' : 'Unknown',
              'Batch Message': batch.message || 'No message'
            });
          });
        } else {
          // If batch has no individual items, export batch-level data
          excelData.push({
            'Batch #': batch.batchIndex,
            'Item #': 'N/A',
            'Name': 'N/A',
            'Phone': 'N/A',
            'Location': 'N/A',
            'Sync Token': 'N/A',
            'Status': batch.status === 10001 ? 'Success' : batch.status === 10002 ? 'Failed' : 'Unknown',
            'Status Code': batch.status,
            'Message': batch.message || 'No message',
            'Batch Status': batch.status === 10001 ? 'Success' : batch.status === 10002 ? 'Failed' : 'Unknown',
            'Batch Message': batch.message || 'No message'
          });
        }
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      ws['!cols'] = [
        { wch: 10 },  // Batch #
        { wch: 10 },  // Item #
        { wch: 30 },  // Name
        { wch: 15 },  // Phone
        { wch: 40 },  // Location
        { wch: 30 },  // Sync Token
        { wch: 12 },  // Status
        { wch: 12 },  // Status Code
        { wch: 50 },  // Message
        { wch: 15 },  // Batch Status
        { wch: 50 },  // Batch Message
      ];

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Beneficiary Upload Results');

      // Generate filename with current date
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `Beneficiary_Upload_Results_${timestamp}.xlsx`;

      // Generate Excel file and trigger download
      XLSX.writeFile(wb, filename);

      cogoToast.success("Excel file exported successfully", {
        hideAfter: 3,
      });
    } catch (error) {
      console.error('Excel export error:', error);
      cogoToast.error("Failed to export Excel file: " + error.message, {
        hideAfter: 5,
      });
    }
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card className="shadow">
            <CardHeader className="bg-primary text-white">
              <h4 className="mb-0">
                <i className="fas fa-upload me-2"></i>
                Beneficiary Data Upload
              </h4>
            </CardHeader>
            <CardBody>
              {/* Component ID Filter */}
              <Row className="mb-3">
                <Col md="12">
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-filter me-2"></i>
                      Filter by Component ID (Optional)
                    </label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="fas fa-id-card"></i>
                      </InputGroupText>
                      <Input
                        type="text"
                        placeholder="Enter Component ID to filter beneficiaries..."
                        value={componentId}
                        onChange={handleComponentIdChange}
                        disabled={isProcessing || !selectedFile}
                      />
                    </InputGroup>
                    <div className="form-text">
                      {componentId ? (
                        <span className="text-info">
                          <i className="fas fa-info-circle me-1"></i>
                          Filtering beneficiaries with component_id: <strong>{componentId}</strong>
                        </span>
                      ) : (
                        <span>Leave empty to process all beneficiaries from the file</span>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Skip Uploaded Status Option */}
              <Row className="mb-3">
                <Col md="12">
                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <Input
                        className="form-check-input"
                        type="checkbox"
                        id="skipUploadedStatus"
                        checked={skipUploadedStatus}
                        onChange={handleSkipUploadedStatusChange}
                        disabled={isProcessing || !selectedFile}
                      />
                      <label className="form-check-label fw-bold" htmlFor="skipUploadedStatus">
                        <i className="fas fa-check-circle me-2"></i>
                        Skip Already Uploaded Records (Status 10001)
                      </label>
                    </div>
                    <div className="form-text">
                      {skipUploadedStatus ? (
                        <span className="text-success">
                          <i className="fas fa-info-circle me-1"></i>
                          Records with status 10001 (already uploaded) will be automatically skipped
                          {skippedCount > 0 && (
                            <strong> ({skippedCount} record{skippedCount !== 1 ? 's' : ''} skipped)</strong>
                          )}
                        </span>
                      ) : (
                        <span className="text-warning">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          All records will be processed, including those already uploaded (status 10001)
                        </span>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* File Upload Section */}
              <Row className="mb-4">
                <Col md="8">
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-file-upload me-2"></i>
                      Select JSON File
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="form-control"
                      accept=".json"
                      onChange={handleFileSelect}
                      disabled={isProcessing}
                    />
                    <div className="form-text">
                      Upload a JSON file containing beneficiary data. The system will process items based on the component_id filter.
                    </div>
                  </div>
                </Col>
                <Col md="4" className="d-flex align-items-end">
                  <Button
                    color="primary"
                    onClick={processFile}
                    disabled={!selectedFile || isProcessing || beneficiaryCount === 0}
                    className="w-100"
                  >
                    {isProcessing ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-play me-2"></i>
                        Process File
                      </>
                    )}
                  </Button>
                </Col>
              </Row>

               {/* File Info */}
               {selectedFile && (
                 <Alert color="info" className="mb-4">
                   <i className="fas fa-info-circle me-2"></i>
                   <strong>Selected File:</strong> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                 </Alert>
               )}

               {/* Beneficiary Count Info */}
               {selectedFile && totalDataCount > 0 && (
                 <Card className="mb-4 border-success">
                   <CardBody>
                     <Row>
                       <Col md="4">
                         <div className="text-center">
                           <h3 className="text-info mb-1">
                             <i className="fas fa-database me-2"></i>
                             {totalDataCount}
                           </h3>
                           <p className="mb-0 text-muted">Total in File</p>
                         </div>
                       </Col>
                       <Col md="4">
                         <div className="text-center">
                           <h3 className={componentId && beneficiaryCount < totalDataCount ? "text-warning mb-1" : "text-success mb-1"}>
                             <i className="fas fa-filter me-2"></i>
                             {beneficiaryCount}
                           </h3>
                           <p className="mb-0 text-muted">
                             {componentId ? `Filtered (${componentId})` : 'To Process'}
                           </p>
                         </div>
                       </Col>
                       <Col md="4">
                         <div className="text-center">
                           <h3 className="text-primary mb-1">
                             <i className="fas fa-layer-group me-2"></i>
                             {totalBatches}
                           </h3>
                           <p className="mb-0 text-muted">Batches</p>
                         </div>
                       </Col>
                     </Row>
                     <div className="text-center mt-3">
                       {componentId ? (
                         <Alert color={beneficiaryCount === 0 ? "warning" : "success"} className="mb-0">
                           <i className={`fas ${beneficiaryCount === 0 ? "fa-exclamation-triangle" : "fa-check-circle"} me-2`}></i>
                           <strong>
                             {beneficiaryCount === 0 
                               ? `No beneficiaries found with component_id: ${componentId}` 
                               : `Ready to Process: ${beneficiaryCount} beneficiary/ies with component_id "${componentId}" will be processed in ${totalBatches} batch/es.`
                             }
                           </strong>
                           {skippedCount > 0 && (
                             <div className="mt-2">
                               <small className="text-muted">
                                 <i className="fas fa-info-circle me-1"></i>
                                 {skippedCount} record{skippedCount !== 1 ? 's' : ''} skipped (status 10001)
                               </small>
                             </div>
                           )}
                         </Alert>
                       ) : (
                         <Alert color="success" className="mb-0">
                           <i className="fas fa-check-circle me-2"></i>
                           <strong>Ready to Process:</strong> The file contains {beneficiaryCount} beneficiaries that will be processed in {totalBatches} batch/es.
                           {skippedCount > 0 && (
                             <div className="mt-2">
                               <small className="text-muted">
                                 <i className="fas fa-info-circle me-1"></i>
                                 {skippedCount} record{skippedCount !== 1 ? 's' : ''} skipped (status 10001 - already uploaded)
                               </small>
                             </div>
                           )}
                         </Alert>
                       )}
                     </div>
                   </CardBody>
                 </Card>
               )}

              {/* Processing Progress */}
              {isProcessing && (
                <Card className="mb-4">
                  <CardBody>
                    <h6 className="mb-3">
                      <i className="fas fa-cogs me-2"></i>
                      Processing Progress
                    </h6>
                    <div className="mb-2">
                      <div className="d-flex justify-content-between">
                        <span>Batch {currentBatch} of {totalBatches}</span>
                        <span>{Math.round(processingProgress)}%</span>
                      </div>
                      <Progress 
                        value={processingProgress} 
                        color="primary" 
                        className="mb-2"
                      />
                    </div>
                    <div className="text-center">
                      <Spinner size="sm" className="me-2" />
                      <span className="text-muted">
                        {isUploading ? 'Uploading data...' : 'Processing batches...'}
                      </span>
                    </div>
                  </CardBody>
                </Card>
              )}

               {/* Results Summary */}
               {batchResults.length > 0 && (
                 <Card className="mb-4">
                   <CardHeader className="bg-light">
                     <h6 className="mb-0">
                       <i className="fas fa-chart-bar me-2"></i>
                       Processing Summary
                     </h6>
                   </CardHeader>
                   <CardBody>
                     <Row>
                       <Col md="3">
                         <div className="text-center">
                           <h4 className="text-primary mb-0">{stats.totalProcessed}</h4>
                           <small className="text-muted">Total Batches</small>
                         </div>
                       </Col>
                       <Col md="3">
                         <div className="text-center">
                           <h4 className="text-success mb-0">{stats.successful}</h4>
                           <small className="text-muted">Successful Items</small>
                         </div>
                       </Col>
                       <Col md="3">
                         <div className="text-center">
                           <h4 className="text-danger mb-0">{stats.failed}</h4>
                           <small className="text-muted">Failed Items</small>
                         </div>
                       </Col>
                       <Col md="3">
                         <div className="text-center">
                           <h4 className="text-info mb-0">
                             {stats.totalItems > 0 ? Math.round((stats.successful / stats.totalItems) * 100) : 0}%
                           </h4>
                           <small className="text-muted">Success Rate</small>
                         </div>
                       </Col>
                     </Row>
                   </CardBody>
                 </Card>
               )}

               {/* Detailed Processing Results */}
               {batchResults.length > 0 && (
                 <Card className="mb-4">
                   <CardHeader className="bg-light">
                     <h6 className="mb-0">
                       <i className="fas fa-list-alt me-2"></i>
                       Detailed Processing Results
                     </h6>
                   </CardHeader>
                   <CardBody>
                     <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                       {batchResults.map((batch, index) => (
                         <Card key={index} className={`mb-3 border-${getStatusColor(batch.status)}`}>
                           <CardHeader className={`bg-${getStatusColor(batch.status)} text-white py-2`}>
                             <div className="d-flex justify-content-between align-items-center">
                               <h6 className="mb-0">
                                 Batch {batch.batchIndex}
                               </h6>
                               {getStatusBadge(batch.status)}
                             </div>
                           </CardHeader>
                           <CardBody className="py-3">
                             <div className="mb-3">
                               <strong>Status:</strong> {batch.status} - {batch.message}
                             </div>
                             
                             {batch.data && batch.data.length > 0 && (
                               <div>
                                 <strong>Individual Results:</strong>
                                 <Table responsive size="sm" className="mt-2">
                                   <thead>
                                     <tr>
                                       <th>Beneficiary Details</th>
                                       <th>Sync Token</th>
                                       <th>Status</th>
                                       <th>Message</th>
                                     </tr>
                                   </thead>
                                   <tbody>
                                     {batch.data.map((item, itemIndex) => (
                                       <tr key={itemIndex} className={item.status === 10002 ? 'table-danger' : item.status === 10001 ? 'table-success' : ''}>
                                         <td>
                                          <div className="small">
                                            <div><strong>Name:</strong> {item.originalDetails?.name || 'Unknown'}</div>
                                            <div><strong>Phone:</strong> {item.originalDetails?.phone || 'Unknown'}</div>
                                            <div><strong>Location:</strong> {item.originalDetails?.location || 'Unknown'}</div>
                                            {(() => {
                                              const criteriaCanProceed =
                                                item.originalDetails?.can_proceed ??
                                                item.criteria_details?.can_proceed ??
                                                item.beneficiary_details?.can_proceed ??
                                                null;

                                              if (criteriaCanProceed === null || criteriaCanProceed === undefined) {
                                                return null;
                                              }

                                              const isPassed = !!criteriaCanProceed;

                                              return (
                                                <div className="mt-1">
                                                  <strong>Criteria:</strong>{' '}
                                                  <span className={`badge bg-${isPassed ? 'success' : 'danger'}`}>
                                                    {isPassed ? 'Passed' : 'Failed'}
                                                  </span>
                                                </div>
                                              );
                                            })()}
                                          </div>
                                         </td>
                                         <td>
                                           <code className="small">
                                             {item.sync_token ? item.sync_token.substring(0, 8) + '...' : 'N/A'}
                                           </code>
                                         </td>
                                         <td>
                                           {getStatusBadge(item.status)}
                                         </td>
                                         <td>
                                           <span className={`text-${getStatusColor(item.status)}`}>
                                             {item.message || 'No message'}
                                           </span>
                                         </td>
                                       </tr>
                                     ))}
                                   </tbody>
                                 </Table>
                               </div>
                             )}
                           </CardBody>
                         </Card>
                       ))}
                     </div>
                   </CardBody>
                 </Card>
               )}

               {/* Failed Records Alert */}
               {failedRecords.length > 0   && (
                 <Alert color="danger" className="mb-4">
                   <div className="d-flex justify-content-between align-items-center">
                     <div>
                       <i className="fas fa-exclamation-triangle me-2"></i>
                       <strong>{failedRecords.length} record{failedRecords.length !== 1 ? 's' : ''} failed</strong> during processing. 
                       Click the download button below to save them as JSON for retry.
                     </div>
                     <Button
                       color="danger"
                       size="sm"
                       onClick={downloadFailedRecords}
                     >
                       <i className="fas fa-download me-1"></i>
                       Download
                     </Button>
                   </div>
                 </Alert>
               )}

               {/* Action Buttons */}
               <div className="d-flex gap-2">
                 <Button
                   color="success"
                   onClick={exportToExcel}
                   disabled={isProcessing || batchResults.length === 0}
                   className="me-2"
                 >
                   <i className="fas fa-file-excel me-2"></i>
                   Export to Excel
                 </Button>
                 <Button
                   color="danger"
                   onClick={downloadFailedRecords}
                   disabled={isProcessing || failedRecords.length === 0}
                   className="me-2"
                 >
                   <i className="fas fa-download me-2"></i>
                   Download Failed Records ({failedRecords.length})
                 </Button>
                 <Button
                   color="secondary"
                   onClick={resetUpload}
                   disabled={isProcessing}
                 >
                   <i className="fas fa-refresh me-2"></i>
                   Reset
                 </Button>
               </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

    </Container>
  );
};

export default UploadBnf;
